/** Locale-aware date/time formatting helpers used across modules. */

const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const shortDate = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const longDate = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
const fullDate = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

export function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function formatTime(iso: string | Date) {
  return time.format(typeof iso === 'string' ? new Date(iso) : iso);
}

/** Message-list stamp: time today, "Yesterday", weekday this week, otherwise date. */
export function formatListStamp(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return time.format(d);
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return 'Yesterday';
  if (now.getTime() - d.getTime() < 6 * 86400000) return weekday.format(d);
  if (d.getFullYear() === now.getFullYear()) return shortDate.format(d);
  return d.toLocaleDateString();
}

export function formatDayDivider(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return `Today · ${shortDateLong(d)}`;
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return `Yesterday · ${shortDateLong(d)}`;
  return longDate.format(d);
}

function shortDateLong(d: Date) {
  return new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric' }).format(d);
}

export function formatFullDate(iso: string) {
  return fullDate.format(new Date(iso));
}

export function formatTimeRange(startIso: string, endIso: string, allDay = false) {
  if (allDay) return 'All day';
  return `${formatTime(startIso)} – ${formatTime(endIso)}`;
}

export function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  return formatListStamp(iso);
}

export function timeZoneLabel(tz = Intl.DateTimeFormat().resolvedOptions().timeZone) {
  try {
    const name = new Intl.DateTimeFormat(undefined, { timeZone: tz, timeZoneName: 'long' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value;
    return name?.replace(/ (Standard|Daylight) Time$/, ' Time') ?? tz;
  } catch {
    return tz;
  }
}

export const localTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
